<template>
	<section>
		<loader :dialog="loading"></loader>
		<floating-alert :message="alertMessage" :show="alertShow" :type="alertType"></floating-alert>

		<v-card elevation="2" rounded="xl">
			<!-- Header -->
			<div class="tr-header">
				<div class="tr-header-left">
					<div class="tr-icon-box">
						<v-icon size="18" color="white">mdi-cash-multiple</v-icon>
					</div>
					<div>
						<p class="tr-title">Liste des transactions</p>
						<p class="tr-subtitle">Historique des paiements</p>
					</div>
				</div>
				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined
					dense
					hide-details
					class="tr-search"
					color="blue darken-2"
				></v-text-field>

				<v-dialog v-model="dialogAddTransaction" max-width="480px">
					<template v-slot:activator="{ on, attrs }">
						<v-btn depressed rounded class="tr-add-btn ml-3" v-bind="attrs" v-on="on">
							<v-icon left small>mdi-plus</v-icon>
							Ajouter
						</v-btn>
					</template>
					<v-card rounded="xl" class="pa-2">
						<div class="tr-dialog-header">
							<div class="tr-icon-box-sm">
								<v-icon size="16" color="white">mdi-cash-plus</v-icon>
							</div>
							<div>
								<p class="tr-dialog-title">Nouvelle transaction</p>
								<p class="tr-dialog-sub">Enregistrer un paiement</p>
							</div>
							<v-spacer></v-spacer>
							<v-btn icon small @click="dialogAddTransaction = false">
								<v-icon color="#6b7280">mdi-close</v-icon>
							</v-btn>
						</div>
						<v-divider></v-divider>
						<v-card-text class="pt-5">
							<v-form ref="form" v-model="valid" lazy-validation>
								<v-select
									@click="showClients"
									v-model="editedItem.customerId"
									:items="clients"
									item-text="fullName"
									item-value="_id"
									label="Client"
									outlined
									dense
									rounded
									prepend-inner-icon="mdi-account"
									hide-details
									class="mb-4"
									:rules="rules"
								></v-select>
								<v-select
									@click="showSubscriptions"
									@change="updateTransactionDetails"
									item-value="_id"
									:items="subscriptions"
									label="Abonnement"
									outlined
									dense
									rounded
									prepend-inner-icon="mdi-playlist-check"
									hide-details
									class="mb-2"
									:rules="rules"
									return-object
								>
									<template #item="{ item }">
										{{ item.label }} — {{ item.type.toLowerCase() }} — {{ item.price }} Fcfa
									</template>
									<template #selection="{ item }">
										{{ item.label }}
									</template>
								</v-select>
							</v-form>
						</v-card-text>
						<v-card-actions class="pa-4 pt-2">
							<v-btn text color="error" rounded @click="dialogAddTransaction = false">Fermer</v-btn>
							<v-spacer></v-spacer>
							<v-btn :disabled="!valid" depressed rounded class="tr-save-btn" @click="save">
								Confirmer
							</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
			</div>

			<v-divider></v-divider>

			<v-data-table
				:headers="headers"
				:items="transactions"
				class="tr-table"
				:loading="loading"
				loading-text="Chargement en cours..."
				:search="search"
				:footer-props="{
					'items-per-page-all-text': 'Tout',
					'items-per-page-text': 'Lignes par page',
				}"
			>
				<template v-slot:[`item.amount`]="{ item }">
					<span class="tr-amount">{{ item.amount }} FCFA</span>
				</template>
				<template v-slot:[`item.actions`]="{ item }">
					<v-btn icon small @click="showDetails(item.subscription)">
						<v-icon size="17" color="#1565c0">mdi-information</v-icon>
					</v-btn>
				</template>
				<template v-slot:[`footer.page-text`]="items">
					{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}
				</template>
				<template v-slot:no-data>
					<v-btn text color="primary" @click="initialize">Actualiser</v-btn>
				</template>
			</v-data-table>
		</v-card>

		<!-- Subscription details dialog -->
		<v-dialog v-model="subscriptionDialog" max-width="400px">
			<v-card rounded="xl" class="pa-2">
				<div class="sub-detail-header">
					<div class="sub-detail-icon">
						<v-icon size="16" color="white">mdi-playlist-check</v-icon>
					</div>
					<div>
						<p class="sub-detail-title">Détails de l'abonnement</p>
						<p class="sub-detail-sub">{{ details.label }}</p>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="subscriptionDialog = false">
						<v-icon color="#6b7280">mdi-close</v-icon>
					</v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pt-4">
					<div class="sub-detail-row">
						<span class="sub-detail-label">Prix</span>
						<span class="sub-detail-value">{{ details.price }} FCFA</span>
					</div>
					<div class="sub-detail-row">
						<span class="sub-detail-label">Durée</span>
						<span class="sub-detail-value">{{ details.duration }} jours</span>
					</div>
					<div class="sub-detail-row">
						<span class="sub-detail-label">Type</span>
						<span class="sub-detail-value">{{ details.type }}</span>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import service from "@/services";
import Loader from "@/components/Loader.vue";
import FloatingAlert from "@/components/FloatingAlert.vue";
export default {
	components: { Loader, FloatingAlert },
	data() {
		return {
			valid: true,
			loadingTable: false,
			dialogAddTransaction: false,
			subscriptionDialog: false,
			search: "",
			details: {},
			transactions: [],
			clients: [],
			subscriptions: [],
			headers: [
				{ text: "Client", value: "customer.fullName" },
				{ text: "Opération", value: "operation" },
				{ text: "Montant", value: "amount" },
				{ text: "Méthode", value: "paymentMethod" },
				{ text: "Date", value: "createdAt" },
				{ text: "Actions", value: "actions", sortable: false, align: "center" },
			],
			editedItem: { price: 0, subscriptionId: "", customerId: "" },
			defaultItem: { price: 0, subscriptionId: "", customerId: "" },
			alertMessage: "",
			alertShow: null,
			alertType: "",
			loading: false,
			rules: [(v) => !!v || "Veuillez remplir ce champ"],
		};
	},
	created() { this.initialize(); },
	methods: {
		manageAlert(display, type, message) { this.alertShow = display; this.alertType = type; this.alertMessage = message; },
		updateTransactionDetails(subscription) { this.editedItem.subscriptionId = subscription._id; this.editedItem.price = subscription.price; },
		showDetails(details) { this.details = details; this.subscriptionDialog = true; },
		showClients() {
			this.loading = true;
			service.getClients().then((r) => { this.clients = r.data?.customers || r.data || []; this.loading = false; }).catch((e) => console.log(e));
		},
		showSubscriptions() {
			this.loading = true;
			service.getSubscriptions().then((r) => { this.subscriptions = r.data; this.loading = false; }).catch((e) => console.log(e));
		},
		initialize() {
			this.loading = true;
			service.getTransactions().then((r) => { this.transactions = r.data?.transactions || r.data || []; this.loading = false; }).catch((e) => { console.log(e); this.loading = false; });
		},
		save() {
			this.alertShow = false;
			if (this.$refs.form.validate()) {
				this.loading = true;
				service.createPayment(this.editedItem).then(() => { this.loading = false; this.dialogAddTransaction = false; this.manageAlert(true, "success", "Paiement ajouté"); this.initialize(); }).catch((e) => { console.log(e); this.loading = false; this.manageAlert(true, "error", e.message); });
			}
		},
	},
};
</script>

<style scoped>
.tr-header { display: flex; align-items: center; padding: 18px 20px 14px; gap: 14px; flex-wrap: wrap; }
.tr-header-left { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 200px; }
.tr-icon-box { width: 38px; height: 38px; border-radius: 10px; background: #00695c; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tr-title { font-size: 0.92rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.tr-subtitle { font-size: 0.72rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.tr-search { max-width: 260px; }
.tr-add-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.tr-table { font-family: Montserrat, sans-serif; }
.tr-amount { font-weight: 700; color: #00695c; }
.tr-dialog-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.tr-icon-box-sm { width: 34px; height: 34px; border-radius: 8px; background: #00695c; display: flex; align-items: center; justify-content: center; }
.tr-dialog-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.tr-dialog-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.tr-save-btn { background: #1565c0 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.sub-detail-header { display: flex; align-items: center; gap: 12px; padding: 16px 20px 12px; }
.sub-detail-icon { width: 32px; height: 32px; border-radius: 8px; background: #1565c0; display: flex; align-items: center; justify-content: center; }
.sub-detail-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.sub-detail-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.sub-detail-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
.sub-detail-row:last-child { border-bottom: none; }
.sub-detail-label { font-size: 0.78rem; color: #6b7280; font-family: Montserrat, sans-serif; }
.sub-detail-value { font-size: 0.82rem; font-weight: 600; color: #111827; font-family: Montserrat, sans-serif; }
</style>

